import React, { useEffect } from "react";
import { useUser } from "../contexts/UserContext";
import { useNavigate, useParams } from "react-router-dom";
import { IRR } from "@formulajs/formulajs";

import { GET_USER_COSTS_AND_BENEFITS_BY_ID, GET_ISCOMPLETED_BY_ID } from "../apollo-queries";
import { CREATE_SUMMARY_MUTATION } from "../apollo-mutations";
import { useMutation, useQuery } from "@apollo/client";
import { Calculations } from "../types/Calculations";
import { useCalculations } from "../contexts/CalculationsContext";

import { Link } from "react-router-dom";
import NavBar from "../components/NavBar";
import { Button } from "@mui/base";
import Title from "../components/Title";
import {
  AssumptionsText,
  NavigationText,
  CostsText,
  ObjectiveText,
  QualitativeText,
  QuantitativeText,
  ReviewText,
} from "../Text";
import { BsArrowLeft } from "react-icons/bs";
import "../images/loader.css";

interface HeaderProps {
  header: string;
  pageLink: string;
}

// Header component with the edit button
const Header: React.FC<HeaderProps> = ({ header, pageLink }) => {
  return (
    <div className="flex mb-1 border-b-2 border-UDig-orange text-UDig-orange font-sans">
      <label className="pb-1 px-1 text-2xl font-bold">{header}</label>
      <Link to={pageLink} className="ml-auto self-end font-semibold">
        {ReviewText.editButton}
      </Link>
    </div>
  );
};

const ReviewScreen: React.FC = () => {
  const { user } = useUser();

  // get uuid from the URL
  const { uuid } = useParams<{ uuid: string }>();
  const userIdString: string = uuid!;

  if (userIdString !== undefined) {
    user.uuid = userIdString;
  }

  const {
    loading: isCompletedLoading,
    error: isCompletedError,
    data: isCompletedData,
  } = useQuery(GET_ISCOMPLETED_BY_ID, {
    variables: { user_id: user.uuid },
    fetchPolicy: "network-only", // stops apollo from getting data from the cache and instead queries the DB
  });

  const navigate = useNavigate();

  const { loading, error, data, refetch } = useQuery(
    GET_USER_COSTS_AND_BENEFITS_BY_ID,
    {
      variables: { user_id: user.uuid },
    }
  );

  const { setCalculations } = useCalculations();

  const Calcs: Calculations = {
    maxYears: 0,
    costTable: [],
    benefitTable: [],
    netCashFlow: [],
    annualIHR: 0.0,
    monthlyIHR: 0.0,
    annualIRR: 0.0,
    monthlyIRR: 0.0,
    irrValid: true,
    discountedCashFlow: [],
    netPresentValue: 0.0,
    totalCostArray: [],
    totalBenefitArray: [],
    netProfitArray: [],
    totalCost: 0.0,
    totalBenefit: 0.0,
    netProfit: 0.0,
    monthlyCumulativeCashFlow: [],
    annualCumulativeCashFlow: [],
    payBackPeriod: "",
    discountedNbArray: [],
    discountedNbArraySum: 0.0,
    roi: 0.0,
    roiValid: true,
  };

  // finds the max number of years in the costs and benefits
  const getMaxYears = () => {
    let tempMax = 1;
    for (let i = 0; i < data.costs.length; i++) {
      if (data.costs[i].number_of_years > tempMax) {
        tempMax = data.costs[i].number_of_years;
      }
    }
    for (let i = 0; i < data.benefits.length; i++) {
      if (data.benefits[i].number_of_years > tempMax) {
        tempMax = data.benefits[i].number_of_years;
      }
    }
    Calcs.maxYears = tempMax;
  };

  // sets up flattened arrays, and fills in zeroes for missing values
  const getCostTable = () => {
    for (let entry = 0; entry < data.costs.length; entry++) {
      const tableEntry = new Array(Calcs.maxYears * 12).fill(0);
      let index = 0;
      for (let year = 0; year < data.costs[entry].number_of_years; year++) {
        for (let month = 0; month < 12; month++) {
          tableEntry[index] =
            Math.round(data.costs[entry].costs_per_month[year][month] * 100) /
            100;
          index++;
        }
      }
      Calcs.costTable.push(tableEntry);
    }
  };

  // sets up flattened arrays, and fills in zeroes for missing values
  const getBenefitTable = () => {
    for (let entry = 0; entry < data.benefits.length; entry++) {
      const tableEntry = new Array(Calcs.maxYears * 12).fill(0);
      let index = 0;
      for (let year = 0; year < data.benefits[entry].number_of_years; year++) {
        for (let month = 0; month < 12; month++) {
          tableEntry[index] =
            Math.round(
              data.benefits[entry].benefits_per_month[year][month] * 100
            ) / 100;
          index++;
        }
      }
      Calcs.benefitTable.push(tableEntry);
    }
  };

  const getNetCashFlow = () => {
    for (let i = 0; i < Calcs.maxYears * 12; i++) {
      let sum = 0;
      for (let j = 0; j < Calcs.costTable.length; j++) {
        sum += -1 * Calcs.costTable[j][i];
      }
      for (let j = 0; j < Calcs.benefitTable.length; j++) {
        sum += Calcs.benefitTable[j][i];
      }
      Calcs.netCashFlow.push(Math.round(sum * 100) / 100);
    }
  };

  // get internal hurdle rate and internal rate of return
  const getIhrIrr = () => {
    Calcs.annualIHR = Number(data?.assumptions[0]?.internalHurdleRate / 100);
    Calcs.monthlyIHR =
      Number(Number(1 + Calcs.annualIHR) ** Number(1 / 12)) - 1;
    let hasNegative = false;
    let hasPositive = false;
    //input error checking
    for (let i = 0; i < Calcs.netCashFlow.length; i++) {
      if (Calcs.netCashFlow[i] < 0) {
        hasNegative = true;
      }
      if (Calcs.netCashFlow[i] > 0) {
        hasPositive = true;
      }
    }
    //calculate or tell summary irrValid is false
    if (hasNegative === true && hasPositive === true) {
      // @ts-ignore - IRR is a function from the formulajs library and wants a guess parameter
      Calcs.monthlyIRR = IRR(Calcs.netCashFlow);
      Calcs.annualIRR = Number(Number(1 + Calcs.monthlyIRR) ** Number(12)) - 1;
    } else {
      Calcs.irrValid = false;
    }
  };

  // get discounted cash flow and net present value
  const getDcfAndNpv = () => {
    for (let i = 0; i < Calcs.netCashFlow.length; i++) {
      let value = Number(
        Calcs.netCashFlow[i] /
          Number(Number(1 + Number(Calcs.monthlyIHR)) ** (i + 1))
      );
      Calcs.netPresentValue += value;
      Calcs.discountedCashFlow.push(Math.round(value * 100) / 100);
    }
    Calcs.netPresentValue = Math.round(Calcs.netPresentValue);
  };

  const getTotalCosts = () => {
    for (let years = 0; years < Calcs.maxYears; years++) {
      let yearlyTotal = 0;
      for (let months = 0; months < 12; months++) {
        for (let i = 0; i < Calcs.costTable.length; i++) {
          yearlyTotal += Calcs.costTable[i][years * 12 + months];
        }
      }
      Calcs.totalCost += yearlyTotal;
      Calcs.totalCostArray.push(Math.round(yearlyTotal));
    }
    Calcs.totalCost = Math.round(Calcs.totalCost);
  };

  const getTotalBenefits = () => {
    for (let years = 0; years < Calcs.maxYears; years++) {
      let yearlyTotal = 0;
      for (let months = 0; months < 12; months++) {
        for (let i = 0; i < Calcs.benefitTable.length; i++) {
          yearlyTotal += Calcs.benefitTable[i][years * 12 + months];
        }
      }
      Calcs.totalBenefit += yearlyTotal;
      Calcs.totalBenefitArray.push(Math.round(yearlyTotal));
    }
    Calcs.totalBenefit = Math.round(Calcs.totalBenefit);
  };

  const getNetProfits = () => {
    for (let i = 0; i < Calcs.totalBenefitArray.length; i++) {
      Calcs.netProfitArray.push(
        Math.round(
          Number(Calcs.totalBenefitArray[i] - Calcs.totalCostArray[i]) * 100
        ) / 100
      );
    }
    Calcs.netProfit = Math.round(Calcs.totalBenefit - Calcs.totalCost);
  };

  const getCumulativeCashFlows = () => {
    Calcs.monthlyCumulativeCashFlow.push(Calcs.netCashFlow[0]);
    for (let i = 1; i < Calcs.netCashFlow.length; i++) {
      Calcs.monthlyCumulativeCashFlow.push(
        Calcs.monthlyCumulativeCashFlow[i - 1] + Calcs.netCashFlow[i]
      );
    }
    for (let i = 11; i < Calcs.monthlyCumulativeCashFlow.length; i = i + 12) {
      Calcs.annualCumulativeCashFlow.push(Calcs.monthlyCumulativeCashFlow[i]);
    }
  };

  const getPayBackPeriod = () => {
    for (let i = 0; i < Calcs.monthlyCumulativeCashFlow.length; i++) {
      if (Calcs.monthlyCumulativeCashFlow[i] >= 0) {
        Calcs.payBackPeriod = "Month " + String(i + 1);
        return;
      }
    }
    Calcs.payBackPeriod = "None";
    return;
  };

  // get discounted net benefit array
  const getDiscountedNbArray = () => {
    for (let year = 0; year < Calcs.maxYears; year++) {
      let sum = 0.0;
      for (let month = 0; month < 12; month++) {
        sum += Calcs.discountedCashFlow[year * 12 + month];
      }
      Calcs.discountedNbArraySum += sum;
      Calcs.discountedNbArray.push(Math.round(sum));
    }
    Calcs.discountedNbArraySum = Math.round(Calcs.discountedNbArraySum);
  };

  const getRoi = () => {
    if (Calcs.totalCost > 0) {
      Calcs.roi = Number(Calcs.netProfit / Calcs.totalCost);
    } else {
      Calcs.roiValid = false;
    }
  };

  useEffect(() => {
    refetch();
  }, [refetch, data]);

  const consoleLog2 = () => {
    console.log("****Console Log 2 Data****");
    console.log(data);
    console.log("Max Years: ");
    console.log(Calcs.maxYears);
    console.log("Costs Table: ");
    console.log(Calcs.costTable);
    console.log("Benefits Table: ");
    console.log(Calcs.benefitTable);
    console.log("Net Cash Flow: ");
    console.log(Calcs.netCashFlow);
    console.log("Annual IHR: ");
    console.log(Calcs.annualIHR);
    console.log("Monthly IHR: ");
    console.log(Calcs.monthlyIHR);
    console.log("Is IRR Valid?: ");
    console.log(Calcs.irrValid);
    console.log("Annual IRR: ");
    console.log(Calcs.annualIRR);
    console.log("Monthly IRR: ");
    console.log(Calcs.monthlyIRR);
    console.log("Discounted Cash Flow: ");
    console.log(Calcs.discountedCashFlow);
    console.log("Net Present Value: ");
    console.log(Calcs.netPresentValue);
    console.log("Total Cost: ");
    console.log(Calcs.totalCost);
    console.log("Total Cost Array: ");
    console.log(Calcs.totalCostArray);
    console.log("Total Benefit: ");
    console.log(Calcs.totalBenefit);
    console.log("Total Benefit Array: ");
    console.log(Calcs.totalBenefitArray);
    console.log("Net Profit: ");
    console.log(Calcs.netProfit);
    console.log("Net Profit Array: ");
    console.log(Calcs.netProfitArray);
    console.log("Monthly Cumulative Cash Flow: ");
    console.log(Calcs.monthlyCumulativeCashFlow);
    console.log("Annual Cumulative Cash Flow: ");
    console.log(Calcs.annualCumulativeCashFlow);
    console.log("Pay Back Period: ");
    console.log(Calcs.payBackPeriod);
    console.log("Discounted Net Profit Array: ");
    console.log(Calcs.discountedNbArray);
    console.log("Discounted Net Profit: ");
    console.log(Calcs.discountedNbArraySum);
    console.log("Is ROI Valid?: ");
    console.log(Calcs?.roiValid);
    console.log("ROI: ");
    console.log(Calcs.roi);
    console.log("********");
    setCalculations(Calcs);
  };

  //Apollo
  const [createSummary] = useMutation(CREATE_SUMMARY_MUTATION);

  // sends summary data into the database
  const handleSaveSummary = () => {
    const summaryData = [
      {
        user_id: user.uuid,
        netCashFlow: Calcs.netCashFlow,
        discountedCashFlow: Calcs.discountedCashFlow,
        annualIhr: Calcs.annualIHR,
        monthlyIhr: Calcs.monthlyIHR,
        annualIrr: Calcs.annualIRR,
        monthlyIrr: Calcs.monthlyIRR,
        irrValid: Calcs.irrValid,
        annualCumulativeCashFlow: Calcs.annualCumulativeCashFlow,
        monthlyCumulativeCashFlow: Calcs.monthlyCumulativeCashFlow,
        totalCost: Calcs.totalCost,
        totalBenefit: Calcs.totalBenefit,
        netProfit: Calcs.netProfit,
        payBackPeriod: Calcs.payBackPeriod,
        roi: Calcs.roi,
        roiValid: Calcs.roiValid,
        maxYears: Calcs.maxYears,
        netProfitArray: Calcs.netProfitArray,
        discountedNbArraySum: Calcs.discountedNbArraySum,
        totalCostArray: Calcs.totalCostArray,
        totalBenefitArray: Calcs.totalBenefitArray,
        netPresentValue: Calcs.netPresentValue,
        discountedNbArray: Calcs.discountedNbArray,
      },
    ];

    createSummary({
      variables: {
        summary: summaryData,
        user_id: user.uuid,
      },
    })
      .then((response) => {
        // Handle the response after the mutation is executed
        console.log(
          "Summary inserted:",
          response.data.insert_summary.returning
        );
      })
      .catch((error) => {
        // Handle errors if the mutation fails
        console.error("Error inserting summary:", error);
      });
  };

  // runs calculations and sends data to the summary page
  const handleNext = () => {
    getMaxYears();
    getCostTable();
    getBenefitTable();
    getNetCashFlow();
    getIhrIrr();
    getDcfAndNpv();
    getTotalCosts();
    getTotalBenefits();
    getNetProfits();
    getCumulativeCashFlows();
    getPayBackPeriod();
    getDiscountedNbArray();
    getRoi();
    // uncomment to see all the calculations
    // consoleLog2();
    handleSaveSummary();
    navigate(`/Summary/${user.uuid}`);
  };

  const handlePrevious = () => {
    navigate(`/Assumptions/${user.uuid}`);
  };
  
  let formatCurrency = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

  //loading gif (app should work fast enough to not need)
  if (loading || data === undefined) return <>loading</>;
  if (isCompletedLoading) return <>Loading...</>;
  return (
    <>
      <div className="hidden laptop:block">
        <NavBar
          activePage="Review"
          completed={isCompletedData?.users[0].is_completed}
        />
      </div>
      <div className="flex justify-center">
        <div className="w-full text-UDig-gray px-10 tablet:px-20 laptop:ml-72 laptop:px-40">
          <Title title={ReviewText.title} subTitle={ReviewText.subTitle} />
          <form className="w-full flex flex-col items-center mt-6">
            {/*Objective Section*/}
            <div className="w-full flex flex-col">
              <Header
                header={ObjectiveText.title}
                pageLink={`/Objective/${user.uuid}`}
              />
              <div>
                {/* Logic for displaying default text or the objective*/}
                {data?.objectives[0]?.description === "" ? (
                  <>{ReviewText.default.objective}</>
                ) : (
                  <div className="ml-1 whitespace-normal mt-2 p-6 rounded-md bg-white drop-shadow-lg">
                    {data?.objectives[0]?.description}
                  </div>
                )}
              </div>
            </div>
            {/*Costs Section*/}
            <div className="w-full flex flex-col mt-6">
              <Header
                header={CostsText.title}
                pageLink={`/Costs/${user.uuid}`}
              />
              <div>
                {/* Logic for displaying default text or costs, if there arent any ongoing or monthly costs then display the defualt message*/}
                {data?.costs.filter((cost) => cost.is_ongoing === true).length +
                  data?.costs.filter((cost) => cost.is_ongoing === false)
                    .length ===
                0 ? (
                  ReviewText.default.costs
                ) : (
                  <>
                    {/* Logic for displaying the Ongoing Costs section header*/}
                    <div className="ml-1 mt-2 text-xl font-semibold">
                      {data?.costs.filter((cost) => cost.is_ongoing === true)
                        .length === 0 ? null : data.costs.filter(
                          (cost) => cost.is_ongoing === true
                        ).length === 1 ? (
                        <div>{CostsText.toggle.ongoing.label}</div>
                      ) : (
                        <div>{CostsText.toggle.ongoing.label}s</div>
                      )}
                    </div>
                    {/* Lists out all the ongoing costs*/}
                    {data?.costs
                      .filter((cost) => cost.is_ongoing === true)
                      .map((item, index) => {
                        return (
                          <div className="mt-2 p-6 rounded-md bg-white drop-shadow-lg">
                            <div className="text-lg font-semibold">
                              {item.category}
                            </div>
                            {/* Logic for displaying the cost's description*/}
                            {item?.description === "" ? null : (
                              <div>{item?.description}</div>
                            )}
                            <div className="flex">
                              <label>{CostsText.yearlyCost.label}: </label>
                              <div className="ml-1 text-UDig-orange">
                                {formatCurrency.format(item.cost_per_year)}
                              </div>
                            </div>
                            <div className="flex">
                              <label>{CostsText.numberOfYears.label}: </label>
                              <div className="ml-1 text-UDig-orange">
                                {item.number_of_years}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {/* Logic for displaying the One-Time Costs section header*/}
                    <div className="ml-1 mt-2 text-xl font-semibold">
                      {data?.costs.filter((cost) => cost.is_ongoing === false)
                        .length === 0 ? null : data.costs.filter(
                          (cost) => cost.is_ongoing === false
                        ).length === 1 ? (
                        <div>{CostsText.toggle.oneTime.label}</div>
                      ) : (
                        <div>{CostsText.toggle.oneTime.label}s</div>
                      )}
                    </div>
                    {/* Lists out all the oneTime costs*/}
                    {data?.costs
                      .filter((cost) => cost.is_ongoing === false)
                      .map((item, index) => {
                        return (
                          <div className="mt-2 p-6 rounded-md bg-white drop-shadow-lg">
                            <div className="text-lg font-semibold">
                              {item.category}
                            </div>
                            {/* Logic for displaying the cost's description*/}
                            {item?.description === "" ? null : (
                              <div>{item?.description}</div>
                            )}
                            <div className="flex flex-col">
                              <div>
                                {Object.values(item.costs_per_month).map(
                                  (monthlyCosts, monthIndex) => (
                                    <>
                                      <div className="font-semibold">
                                        Year {" " + String(monthIndex + 1)}
                                      </div>
                                      <div
                                        className="w-full grid grid-cols-2 gap-y-1 tablet:grid-cols-3 laptop:grid-cols-4"
                                        key={monthIndex}
                                      >
                                        {monthlyCosts.map((cost, costIndex) => (
                                          <div
                                            className="flex flex-col flex-nowrap"
                                            key={costIndex}
                                          >
                                            Month {" " + String(costIndex + 1)}
                                            <div className="text-UDig-orange">
                                              {formatCurrency.format(cost)}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </>
                )}
              </div>
            </div>
            {/*Quantitative Section*/}
            <div className="w-full flex flex-col mt-6">
              <Header
                header={QuantitativeText.title}
                pageLink={`/Quantitative/${user.uuid}`}
              />
              <div>
                {/* Logic for displaying default text or financial benefits*/}
                {data.benefits.filter((benefit) => benefit.is_ongoing === true)
                  .length +
                  data.benefits.filter(
                    (benefit) => benefit.is_ongoing === false
                  ).length ===
                0 ? (
                  ReviewText.default.quantitative
                ) : (
                  <>
                    {/* Logic for displaying the Ongoing Benefit section header*/}
                    <div className="ml-1 mt-2 text-xl font-semibold">
                      {data.benefits.filter(
                        (benefit) => benefit.is_ongoing === true
                      ).length === 0 ? null : data.benefits.filter(
                          (benefit) => benefit.is_ongoing === false
                        ).length === 1 ? (
                        <div>{QuantitativeText.toggle.ongoing.label}</div>
                      ) : (
                        <div>{QuantitativeText.toggle.ongoing.label}s</div>
                      )}
                    </div>
                    {/* Lists out all the ongoing Benefit*/}
                    {data.benefits
                      .filter((benefit) => benefit.is_ongoing === true)
                      .map((item, index) => {
                        return (
                          <div className="mt-2 p-6 rounded-md bg-white drop-shadow-lg">
                            <div className="text-lg font-semibold">
                              {item.category}
                            </div>
                            {/* Logic for displaying the cost's description*/}
                            {item?.description === "" ? null : (
                              <div>{item?.description}</div>
                            )}
                            <div className="flex">
                              <label>
                                {QuantitativeText.yearlyBenefit.label}:
                              </label>
                              <div className="ml-1 text-UDig-orange">
                                {formatCurrency.format(item.benefit_per_year)}
                              </div>
                            </div>
                            <div className="flex">
                              <label>
                                {QuantitativeText.numberOfYears.label}:
                              </label>
                              <div className="ml-1 text-UDig-orange">
                                {item.number_of_years}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {/* Logic for displaying the One-Time benefit section header*/}
                    <div className="ml-1 mt-2 text-xl font-semibold">
                      {data.benefits.filter(
                        (benefit) => benefit.is_ongoing === false
                      ).length === 0 ? null : data.benefits.filter(
                          (benefit) => benefit.is_ongoing === false
                        ).length === 1 ? (
                        <div>{QuantitativeText.toggle.oneTime.label}</div>
                      ) : (
                        <div>{QuantitativeText.toggle.oneTime.label}s</div>
                      )}
                    </div>
                    {/* Lists out all the oneTime benefits*/}
                    {data?.benefits
                      .filter((benefit) => benefit.is_ongoing === false)
                      .map((item, index) => {
                        return (
                          <div className="mt-2 p-6 rounded-md bg-white drop-shadow-lg">
                            <div className="text-lg font-semibold">
                              {item.category}
                            </div>
                            {/* Logic for displaying the cost's description*/}
                            {item?.description === "" ? null : (
                              <div>{item?.description}</div>
                            )}
                            <div className="flex flex-col">
                              <div className="">
                                {Object.values(item.benefits_per_month).map(
                                  (monthlyBenefits, monthIndex) => (
                                    <>
                                      <div className="mt-2 font-semibold">
                                        Year {" " + String(monthIndex + 1)}
                                      </div>
                                      <div
                                        className="w-full grid grid-cols-2 gap-y-1 tablet:grid-cols-3 laptop:grid-cols-4"
                                        key={monthIndex}
                                      >
                                        {monthlyBenefits.map(
                                          (benefit, benefitIndex) => (
                                            <div
                                              className="flex flex-col flex-nowrap"
                                              key={benefitIndex}
                                            >
                                              Month{" "}
                                              {" " + String(benefitIndex + 1)}
                                              <div className="text-UDig-orange">
                                                {formatCurrency.format(benefit)}
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </>
                )}
              </div>
            </div>
            {/*Qualitative Section*/}
            <div className="w-full flex flex-col mt-6">
              <Header
                header={QualitativeText.title}
                pageLink={`/Qualitative/${user.uuid}`}
              />
              <div className="ml-1">
                {data.qualitative_benefits.length === 0 ||
                data.qualitative_benefits[0].category === "" ? (
                  <>{ReviewText.default.qualitative}</>
                ) : (
                  <>
                    {data.qualitative_benefits.map((item) => {
                      return (
                        <div className="mt-4 p-6 rounded-md bg-white drop-shadow-lg flex flex-col">
                          <div className="text-lg font-semibold">
                            {item.category}
                          </div>
                          <ul className="ml-5 list-disc">
                            {item.benefits.map((benefit) => {
                              return <li>{benefit}</li>;
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
            {/*Assumptions Section*/}
            <div className="w-full flex flex-col mt-6">
              <Header
                header={AssumptionsText.title}
                pageLink={`/Assumptions/${user.uuid}`}
              />
              <div className=" ml-1 mt-2 p-6 rounded-md bg-white drop-shadow-lg whitespace-normal">
                <div className="flex items-center">
                  <label>{AssumptionsText.internalHurdleRate.label}: </label>
                  <div className="ml-1 text-UDig-orange">
                    {data?.assumptions[0]?.internalHurdleRate}%
                  </div>
                </div>
                {data?.assumptions[0]?.description === "" ? null : (
                  <div>{data?.assumptions[0]?.description}</div>
                )}
              </div>
            </div>
          </form>
          <div className="mt-4 mb-20 flex justify-between text-UDig-orange">
            <Button className="flex items-center" onClick={handlePrevious}>
              <BsArrowLeft className="mr-2" />
              {NavigationText.previous}
            </Button>
            <Button
              className="flex items-center border-2 border-UDig-orange bg-UDig-orange text-white px-2 py-1 rounded-md text-UDig-orange font-semibold shadow-md hover:bg-white hover:text-UDig-orange hover:border-2"
              onClick={() => handleNext()}
              type="button"
            >
              {ReviewText.submitButton}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewScreen;
